import React, { Fragment } from "react";
import styled from "styled-components";
import { connect } from "react-redux";
import { compose, withStateHandlers } from "recompose";
import { Link } from "react-router-dom";
import {
  Classes,
  CollapsibleList,
  Colors,
  MenuItem,
  Tab2,
  Tabs2
} from "@blueprintjs/core";
import Layout, { Map } from "app/layout/Layout";
import TinyLayout, { Map as TinyMap } from "app/layout/TinyLayout";
import MainMenu from "app/MainMenu";
import Footer from "app/Footer";
import TopicMenu from "./TopicMenu";
import { sizes, getScreenCode } from "app/media";
import Iframe from "./CesiumIframe";
import { injectIntl, FormattedMessage } from "react-intl";

const fgColor = Colors.DARK_GRAY5;
const bgColor = Colors.LIGHT_GRAY3;
const borderColor = Colors.GRAY5;

const Sidebar = styled.div`
  border-left: 1px solid ${borderColor};
  color: ${fgColor};
  background-color: ${bgColor};
  flex: 0 0 350px;
  min-height: 100%;
  & h5,
  & h6 {
    color: ${fgColor};
  }
`;

const FullSidebar = Sidebar.extend`
  border-left: 0px;
`;

const renderBreadcrumb = props => {
  if (props.href != null) {
    return (
      <Link className={Classes.BREADCRUMB} to={props.href}>
        {props.text}
      </Link>
    );
  } else {
    return (
      <span className={`${Classes.BREADCRUMB} ${Classes.BREADCRUMB_CURRENT}`}>
        {props.text}
      </span>
    );
  }
};

const Breadcrumbs = props => {
  const { topic, visibleItemCount, maxTopicTitleLength, intl } = props;
  const topicTitle =
    topic.title.length > maxTopicTitleLength
      ? topic.title.substr(0, maxTopicTitleLength) + "..."
      : topic.title;
  const urlSearch = window.location.search;

  return (
    <CollapsibleList
      className={Classes.BREADCRUMBS}
      dropdownTarget={<span className={Classes.BREADCRUMBS_COLLAPSED} />}
      visibleItemCount={visibleItemCount}
      renderVisibleItem={renderBreadcrumb}
    >
      <MenuItem
        text={intl.formatMessage({
          id: "app.topics",
          defaultMessage: "Topics"
        })}
        href={"/topics" + urlSearch}
      />
      <MenuItem text={topicTitle || topic._id} />
    </CollapsibleList>
  );
};

const Tabs3 = styled(Tabs2).attrs({
  className: "map-tabs"
})`
  height: 100vh;
  & .pt-tab-list {
    background-color: white;
    justify-content: space-around;
    box-shadow: 0 0 0 1px rgba(16, 22, 26, 0.1), 0 0 0 rgba(16, 22, 26, 0),
      0 1px 1px rgba(16, 22, 26, 0.2);
    z-index: 1000;
  }
  & .pt-tab {
    line-height: 50px;
    min-width: 46vw;
    text-align: center;
  }
  & .pt-tab-panel {
    margin: 0;
    height: calc(100% - 50px);
  }
`;

const Frame3D = styled(Iframe)`
  background: #222;
  width: 100%;
  height: 100%;
  border: none;
`;

const ExternalFrame3D = styled.iframe`
  background: #222;
  width: 100%;
  height: 100%;
  border: none;
`;

const Map3D = injectIntl(props => {
  const { intl, ...rest } = props;
  return (
    <Map {...rest}>
      {props.config && typeof props.config.url === "string" ? (
        <ExternalFrame3D
          title={intl.formatMessage({
            id: "app.3dview",
            defaultMessage: "3D View"
          })}
          src={props.config.url}
        >
          <FormattedMessage id="app.3dview" defaultMessage="3D View" />
        </ExternalFrame3D>
      ) : (
        <Frame3D
          title={intl.formatMessage({
            id: "app.3dview",
            defaultMessage: "3D View"
          })}
          src={"/minivis3d/"}
          tilesets={props.tileSets}
          // this can happen if a model is uploaded by no config saved
          config={props.config ? props.config : {}}
          proposals={props.proposals ? props.proposals : []}
          bounds={props.bounds}
        >
          <FormattedMessage id="app.3dview" defaultMessage="3D View" />
        </Frame3D>
      )}
    </Map>
  );
});

const MapTabs = injectIntl(props => {
  const { intl, is3d } = props;
  return (
    <Tabs3>
      <Tab2
        id="objects"
        title={intl.formatMessage({
          id: "app.mapview",
          defaultMessage: "Map View"
        })}
        panel={<TinyMap>{props.children}</TinyMap>}
      />
      <Tab2
        id="description"
        title={intl.formatMessage({
          id: "app.3dview",
          defaultMessage: "3D View"
        })}
        panel={
          <Map3D
            style={{ display: !is3d ? "none" : undefined }}
            config={props.config}
            tileSets={props.tileSets}
            bounds={props.bounds}
          />
        }
      />
    </Tabs3>
  );
});

const Tabs4 = styled(Tabs2)`
  margin-right: 20px;
  & .pt-tab {
    line-height: 50px;
  }
`;

const handleTabChange = (set2d, set3d) => (newTabId, prevTabId, event) => {
  if (newTabId === "3d") {
    set3d();
  } else {
    set2d();
  }
};

const DesktopMapTabs = injectIntl(props => {
  const { set2d, set3d, intl } = props;
  return (
    <Tabs4 onChange={handleTabChange(set2d, set3d)}>
      <Tab2
        id="2d"
        title={intl.formatMessage({
          id: "app.mapview",
          defaultMessage: "Map View"
        })}
      />
      <Tab2
        id="3d"
        title={intl.formatMessage({
          id: "app.3dview",
          defaultMessage: "3D View"
        })}
      />
    </Tabs4>
  );
});

const DesktopMap = props => {
  const is3d = props.is3d;
  return (
    <Fragment>
      <Map style={{ display: is3d ? "none" : undefined }}>{props.children}</Map>
      <Map3D
        style={{ display: !is3d ? "none" : undefined }}
        config={props.config}
        tileSets={props.tileSets}
        proposals={props.proposals}
        bounds={props.bounds}
      />
    </Fragment>
  );
};

const TopicLayout = props => {
  const {
    main,
    topicMap,
    sidebar,
    topicId,
    topic,
    history,
    screen,
    is3d,
    set3d,
    set2d,
    maxTopicTitleLength,
    intl,
    proposals,
    bounds
  } = props;
  const visibleItemCount = screen < sizes.medium ? 3 : 4;
  const asMenu = screen <= sizes.xsmall;
  let ChosenLayout = Layout;
  const has3d =
    (topic &&
      topic.config &&
      topic.config["3dModel"] &&
      topic.config["3dModel"].link) ||
    (topic &&
      topic.config &&
      topic.config["3dViewConfig"] &&
      topic.config["3dViewConfig"].url)
      ? true
      : false;
  let ChosenMap = has3d ? DesktopMap : Map;
  let ChosenSidebar = Sidebar;
  if (screen === sizes.tiny) {
    ChosenLayout = TinyLayout;
    ChosenMap = has3d ? MapTabs : TinyMap;
    ChosenSidebar = FullSidebar;
  }
  const breadcrumbs = topic && (
    <Breadcrumbs
      visibleItemCount={visibleItemCount}
      topic={topic}
      history={history}
      topicId={topicId}
      maxTopicTitleLength={maxTopicTitleLength}
      intl={intl}
    />
  );
  return (
    <ChosenLayout
      breadcrumbs={breadcrumbs}
      navbar={
        <MainMenu
          breadcrumbs={breadcrumbs}
          topicId={topicId}
          desktopExtra={
            has3d ? (
              <Fragment>
                <DesktopMapTabs is3d={is3d} set2d={set2d} set3d={set3d} />
                <span className="pt-navbar-divider" />
              </Fragment>
            ) : (
              undefined
            )
          }
          contextMenu={
            topicId && (
              <TopicMenu
                topicId={topicId}
                asMenu={asMenu}
                history={history}
                screen={screen}
                topicTitle={topic && topic.title}
              />
            )
          }
        />
      }
      main={
        main ? (
          main
        ) : (
          <ChosenMap
            is3d={is3d}
            tileSets={
              has3d && {
                tiles: topic.config["3dModel"].link,
                buildings: topic.config["3dModel"].link
              }
            }
            proposals={proposals && proposals.proposalsById}
            bounds={bounds}
            config={has3d && topic.config["3dViewConfig"]}
          >
            {topicMap}{" "}
          </ChosenMap>
        )
      }
      sidebar={sidebar && <ChosenSidebar>{sidebar}</ChosenSidebar>}
      footer={<Footer />}
    />
  );
};

const mapScreenState = state => {
  return { screen: getScreenCode(state.responsive), bounds: state.map.settings.bounds };
};

export default compose(
  connect(mapScreenState),
  withStateHandlers(
    { is3d: false },
    {
      set3d: () => () => ({ is3d: true }),
      set2d: () => () => ({ is3d: false })
    }
  ),
  injectIntl
)(TopicLayout);



// WEBPACK FOOTER //
// ./src/app/topics/TopicLayout.js